import React, { createContext, useCallback, useContext, useState } from "react";

interface UploadState {
  isUploading: boolean;
  progress: number;
  fileName?: string;
  phase?: "trimming" | "uploading" | "processing" | "done";
  error?: string;
}

interface UploadContextType {
  uploadState: UploadState | null;
  startUpload: (fileName?: string) => void;
  updateProgress: (progress: number, phase?: UploadState["phase"]) => void;
  completeUpload: () => void;
  failUpload: (error: string) => void;
  clearUpload: () => void;
}

const UploadContext = createContext<UploadContextType | undefined>(undefined);

export const UploadProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [uploadState, setUploadState] = useState<UploadState | null>(null);

  const startUpload = useCallback((fileName?: string) => {
    setUploadState({
      isUploading: true,
      progress: 0,
      fileName,
      phase: "uploading",
    });
  }, []);

  const updateProgress = useCallback(
    (progress: number, phase?: UploadState["phase"]) => {
      setUploadState(prev =>
        prev
          ? {
              ...prev,
              progress: Math.min(100, Math.max(0, progress)),
              ...(phase ? { phase } : {}),
            }
          : null
      );
    },
    []
  );

  const completeUpload = useCallback(() => {
    setUploadState(prev =>
      prev
        ? {
            ...prev,
            isUploading: false,
            progress: 100,
            phase: "done",
          }
        : null
    );
    // Automatisch nach 3 Sekunden ausblenden
    setTimeout(() => {
      setUploadState(null);
    }, 3000);
  }, []);

  const failUpload = useCallback((error: string) => {
    setUploadState(prev =>
      prev
        ? {
            ...prev,
            isUploading: false,
            error,
          }
        : null
    );
    // Automatisch nach 5 Sekunden ausblenden
    setTimeout(() => {
      setUploadState(null);
    }, 5000);
  }, []);

  const clearUpload = useCallback(() => {
    setUploadState(null);
  }, []);

  return (
    <UploadContext.Provider
      value={{
        uploadState,
        startUpload,
        updateProgress,
        completeUpload,
        failUpload,
        clearUpload,
      }}
    >
      {children}
    </UploadContext.Provider>
  );
};

export const useUpload = (): UploadContextType => {
  const context = useContext(UploadContext);
  if (context === undefined) {
    throw new Error("useUpload must be used within an UploadProvider");
  }
  return context;
};
